import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { createTheme } from '@mui/material/styles';
import { ThemeProvider, CssBaseline, useMediaQuery, GlobalStyles } from '@mui/material';


const ColorModeContext = createContext(null);
export const useColorMode = () => useContext(ColorModeContext);

const lightModeBackgroundImage = `
  radial-gradient(36% 34% at 17% 11%, rgba(225, 166, 249, 0.5) 0%, rgba(225, 166, 249, 0) 72%),
  radial-gradient(44% 42% at 20% 92%, rgba(188, 129, 248, 0.48) 0%, rgba(188, 129, 248, 0) 76%),
  radial-gradient(46% 44% at 55% 52%, rgba(189, 159, 250, 0.56) 0%, rgba(189, 159, 250, 0) 72%),
  radial-gradient(30% 28% at 50% 8%, rgba(199, 170, 246, 0.44) 0%, rgba(199, 170, 246, 0) 75%),
  linear-gradient(180deg, #f8f4fb 0%, #efe8f6 100%)
`;

const toolScrollbarStyles = {
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(148, 163, 184, 0.55) transparent',
  '&::-webkit-scrollbar': {
    width: 8,
    height: 8,
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(148, 163, 184, 0.45)',
    borderRadius: 999,
    border: '2px solid transparent',
    backgroundClip: 'content-box',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: 'rgba(100, 116, 139, 0.75)',
  },
  '&::-webkit-scrollbar-corner': {
    backgroundColor: 'transparent',
  },
};

const createJmgTheme = (mode) => {
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      primary: { main: '#CA1DEF' },
      secondary: { main: '#F4E1F9' },
      background: {
        default: isDark ? '#05040A' : '#F4E1F9',
        paper: isDark ? '#141020' : '#FFFFFF'
      },
      text: {
        primary: isDark ? '#FFFFFF' : '#14001F',
        secondary: isDark ? '#F4E1F9' : '#4A345C'
      }
    },
    typography: {
      fontFamily: [
        'Montserrat',
        'system-ui',
        '-apple-system',
        'BlinkMacSystemFont',
        'Segoe UI',
        'sans-serif'
      ].join(','),
      h4: { fontWeight: 700 },
      h6: { fontWeight: 600 },
      body1: { fontWeight: 500 },
      body2: { fontWeight: 400 }
    },
    shape: { borderRadius: 12 },
    zIndex: {
      tooltip: 3400,
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: isDark
                ? 'linear-gradient(90deg, #05040A 0%, #14001F 50%, #301B4D 100%)'
                : 'linear-gradient(90deg, #FFFFFF 0%, #F4E1F9 40%, #FFFFFF 100%)',
            boxShadow: '0 0 24px rgba(0, 0, 0, 0.45)',
            color: isDark ? '#FFFFFF' : '#14001F'   // <- NEU: Text/Icon-Farbe
          }
        }
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: isDark ? '#05040A' : '#F4E1F9',
            backgroundImage: isDark ? 'none' : lightModeBackgroundImage,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundAttachment: isDark ? 'scroll' : 'fixed'
          },
          '#root': {
            background: 'transparent'
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: { borderRadius: 16 }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: { borderRadius: 16 }
        }
      },
      MuiPopover: {
        styleOverrides: {
          root: ({ theme }) => ({
            zIndex: (theme.zIndex?.modal ?? 1300) + 3000
          })
        }
      }
    }
  });
};

export const AppThemeProvider = ({ children }) => {
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');

  const [modeSetting, setModeSetting] = useState(() => {
    if (typeof window === 'undefined') return 'system';
    return localStorage.getItem('jmg-theme-mode') || 'system';
  });

  const effectiveMode = useMemo(() => {
    if (modeSetting === 'system') {
      return prefersDark ? 'dark' : 'light';
    }
    return modeSetting;
  }, [modeSetting, prefersDark]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('jmg-theme-mode', modeSetting);
    }
  }, [modeSetting]);

  const theme = useMemo(() => createJmgTheme(effectiveMode), [effectiveMode]);

  const value = useMemo(
    () => ({
      mode: effectiveMode,
      modeSetting,
      setModeSetting
    }),
    [effectiveMode, modeSetting]
  );

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <GlobalStyles
          styles={(t) => ({
            '*': toolScrollbarStyles,
            ".MuiDataGrid-root, .MuiDataGrid-panel, .MuiDataGrid-menu": {
              "--DataGrid-t-z-index-panel": String((t.zIndex?.modal ?? 1300) + 3000),
              "--DataGrid-t-z-index-menu": String((t.zIndex?.modal ?? 1300) + 3000),
            },
            ".MuiDataGrid-panel, .MuiDataGrid-menu": {
              zIndex: `${(t.zIndex?.modal ?? 1300) + 3000} !important`,
            },
            ".MUIDataTableBodyRow-root": {
              "--mui-dt-row-bg": t.palette.background.paper,
            },
            ".MUIDataTableBodyRow-root:hover": {
              "--mui-dt-row-bg": t.palette.action.hover,
            },
            ".MUIDataTableBodyRow-root > .MuiTableCell-root": {
              backgroundColor: "var(--mui-dt-row-bg)",
            },
            ".MUIDataTableBodyRow-root > .MuiTableCell-root:first-of-type": {
              backgroundColor: "var(--mui-dt-row-bg)",
            },
          })}
        />
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};
